/**
 * Which window keydowns are game keys (q-p2triage-fe-player-trust-and-ux). Three
 * window listeners (movement `InputState`, the panel hotkeys in `Shell`, the
 * dungeon G in `DungeonRunClient`) used to act on every key — so typing "wasd"
 * in chat walked the character, "g" in a mail body entered a dungeon, and
 * Ctrl+C / Ctrl+P toggled panels instead of copying / printing. Each now asks
 * these first. Duck-typed on the event target so it runs under `node --test`
 * with no DOM. Pure.
 */

/** The parts of a DOM element this reads. */
interface TargetLike {
  tagName?: unknown;
  isContentEditable?: unknown;
}

const EDITABLE_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

/** True when keystrokes on `target` belong to a text field, select or editable region. */
export function isEditableTarget(target: unknown): boolean {
  if (target === null || typeof target !== 'object') return false;
  const t = target as TargetLike;
  if (t.isContentEditable === true) return true;
  return typeof t.tagName === 'string' && EDITABLE_TAGS.has(t.tagName.toUpperCase());
}

/** The parts of a `KeyboardEvent` this reads. */
export interface KeyLike {
  target?: unknown;
  ctrlKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
  isComposing?: boolean;
  keyCode?: number;
}

/** A browser/OS chord (Ctrl/Cmd/Alt held): never a bare game key. Shift is not a chord. */
export function hasCommandModifier(e: KeyLike): boolean {
  return e.ctrlKey === true || e.metaKey === true || e.altKey === true;
}

/**
 * An IME is mid-composition: the Enter that commits a candidate must not also send
 * the half-typed text. `keyCode` 229 is the legacy signal some browsers still use.
 */
export function isImeComposing(e: KeyLike): boolean {
  return e.isComposing === true || e.keyCode === 229;
}

/** A keydown the game may act on: not typed into a field, not a chord, not an IME commit. */
export function isGameKey(e: KeyLike, activeElement?: unknown): boolean {
  if (isImeComposing(e) || hasCommandModifier(e)) return false;
  return !isEditableTarget(e.target) && !isEditableTarget(activeElement);
}
